import {
  ActionIcon,
  Badge,
  Button,
  Checkbox,
  Divider,
  FileButton,
  Group,
  Image,
  List,
  Modal,
  Skeleton,
  Stack,
  Text,
  TextInput,
  Tooltip
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconCheck,
  IconCrown,
  IconDeviceFloppy,
  IconFileImport,
  IconTrash,
  IconUpload,
  IconWorld
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api';
import { useAuth } from '../hooks/useAuth';
import { useTemplates } from '../hooks/useTemplates';
import { useVariables } from '../hooks/useVariables';
import { Template } from '../types/template.types';

interface ThumbnailData {
  items: Array<{
    id: string;
    type: string;
    content: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    style: any;
    zIndex: number;
  }>;
  width: number;
  height: number;
}

interface ThumbnailResponse {
  success: boolean;
  url: string;
  error?: string;
}

export function TemplateManager() {
  const [opened, { open, close }] = useDisclosure(false);
  const [saveModalOpened, setSaveModalOpened] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null); 
  const [previewOpened, setPreviewOpened] = useState(false);
  const [includePublic, setIncludePublic] = useState(true);
  const [viewAllTemplates, setViewAllTemplates] = useState(false);
  
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const { variables, setVariables } = useVariables();
  
  const { 
    templates, 
    isLoading, 
    fetchTemplates, 
    saveTemplate, 
    applyTemplate, 
    deleteTemplate,
    togglePublicTemplate
  } = useTemplates();

  // Lọc templates theo từ khóa tìm kiếm
  const filteredTemplates = templates.filter(template => 
    template.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Lấy danh sách templates khi component mount hoặc khi các tùy chọn thay đổi
  useEffect(() => {
    if (opened) {
      fetchTemplates(includePublic);
    }
  }, [opened, includePublic, viewAllTemplates]);

  // Thêm CSS styles khi component mount
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      .template-item:hover {
        background-color: #f1f1f1 !important;
      }
      .template-item.selected {
        background-color: #e6f7ff !important;
        border: 1px solid #91d5ff;
      }
    `;
    document.head.appendChild(styleElement);
    
    // Cleanup khi component unmount
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const openSaveModal = () => {
    setTemplateName('');
    setSelectedTemplateId(null);
    setSelectedTemplate(null);
    setSaveModalOpened(true);
    fetchTemplates(false); // Gọi API để lấy danh sách template của người dùng
  };

  const handleSaveTemplate = async () => {
    if (selectedTemplateId) {
      // Lưu đè lên template đã chọn
      await saveTemplate(selectedTemplate?.name || '', selectedTemplateId, variables);
    } else {
      if (templateName.trim()) {
        await saveTemplate(templateName, undefined, variables);
      } else {
        return; // Không làm gì nếu tên trống
      }
    }
    
    setTemplateName('');
    setSelectedTemplateId(null);
    setSaveModalOpened(false);
  };

  // Xử lý áp dụng template
  const handleApplyTemplate = async (templateId: string) => {
    const template = await applyTemplate(templateId);
    
    // Nếu template có biến, cập nhật vào store
    if (template && template.variables && Array.isArray(template.variables)) {
      setVariables(template.variables);
    }
    
    close();
  };

  // Xử lý xóa template
  const handleDeleteTemplate = async (templateId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    if (window.confirm('Bạn có chắc muốn xóa template này?')) {
      await deleteTemplate(templateId);
    }
  };

  // Xử lý công khai/riêng tư template (chỉ dành cho admin)
  const handleTogglePublic = async (templateId: string, isPublic: boolean, event: React.MouseEvent) => {
    event.stopPropagation();
    await togglePublicTemplate(templateId, isPublic);
  };

  // Xử lý khi chọn template trong modal lưu
  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
    setSelectedTemplateId(template._id);
  };

  // Xử lý upload thumbnail
  const handleUploadThumbnail = async (file: File | null, templateId: string) => {
    if (!file) return;
    
    try {
      // Kiểm tra kích thước file
      if (file.size > 5 * 1024 * 1024) { // 5MB
        notifications.show({
          title: 'Lỗi',
          message: 'File quá lớn, vui lòng chọn file nhỏ hơn 5MB',
          color: 'red'
        });
        return;
      }
      
      // Kiểm tra loại file
      if (!file.type.startsWith('image/')) {
        notifications.show({
          title: 'Lỗi',
          message: 'Vui lòng chọn file hình ảnh',
          color: 'red'
        });
        return;
      }
      
      // Tạo FormData để gửi file
      const formData = new FormData();
      formData.append('thumbnail', file);
      
      // Gọi API upload file
      const response = await fetch(`${BASE_URL}api/templates/${templateId}/thumbnail`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        notifications.show({
          title: 'Thành công',
          message: 'Đã tải lên hình đại diện cho template',
          color: 'green'
        });
        
        // Cập nhật lại danh sách templates
        await fetchTemplates(includePublic);
        return true;
      } else {
        throw new Error(result.error || 'Không thể tải lên hình đại diện');
      }
    } catch (error) {
      console.error('Upload thumbnail error:', error);
      notifications.show({
        title: 'Lỗi',
        message: error instanceof Error ? error.message : 'Đã xảy ra lỗi khi tải lên hình đại diện',
        color: 'red'
      });
      return false;
    }
  };
  
  // Mở modal xem trước ảnh
  const openPreviewImage = (imageUrl: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setPreviewImage(imageUrl);
    setPreviewOpened(true);
  };

  // Định dạng ngày giờ
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <>
      <Group>
        <Button
          leftSection={<IconFileImport size={16} />}
          onClick={open}
          variant="light"
        >
          Danh sách mẫu
        </Button>
        <Button
          leftSection={<IconDeviceFloppy size={16} />}
          onClick={openSaveModal}
          variant="outline"
        >
          Lưu mẫu
        </Button>
      </Group>

      <Modal
        opened={opened}
        onClose={close}
        title="Mẫu đã lưu"
        size="lg"
      >
        <Stack>
          <Group>
            <TextInput
              placeholder="Tìm kiếm mẫu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ flex: 1 }}
            />
            
            <Checkbox
              label="Bao gồm mẫu công khai"
              checked={includePublic}
              onChange={(e) => setIncludePublic(e.currentTarget.checked)}
            />
            
            {isAdmin && (
              <Checkbox
                label="Xem tất cả mẫu"
                checked={viewAllTemplates}
                onChange={(e) => {
                  setViewAllTemplates(e.currentTarget.checked);
                  fetchTemplates(includePublic);
                }}
                color="green"
              />
            )}
          </Group>
          
          <Divider />
          
          {isLoading ? (
            <Stack>
              <Skeleton height={50} radius="sm" />
              <Skeleton height={50} radius="sm" />
              <Skeleton height={50} radius="sm" />
            </Stack>
          ) : filteredTemplates.length > 0 ? (
            <List spacing="xs">
              {filteredTemplates.map((template: Template) => (
                <List.Item 
                  key={template._id}
                  icon={
                    <Group>
                      {/* Chỉ hiển thị nút xóa nếu người dùng là chủ sở hữu hoặc admin */}
                      {(template.isOwner || isAdmin) && (
                        <ActionIcon 
                          color="red" 
                          variant="subtle"
                          onClick={(e) => handleDeleteTemplate(template._id, e)}
                          title="Xóa template"
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      )}
                      
                      {/* Nút upload thumbnail chỉ cho phép chủ sở hữu hoặc admin */}
                      {(template.isOwner || isAdmin) && (
                        <FileButton 
                          onChange={(file) => handleUploadThumbnail(file, template._id)}
                          accept="image/png,image/jpeg,image/webp"
                        >
                          {(props) => (
                            <ActionIcon
                              color="blue"
                              variant="subtle"
                              title="Tải lên hình đại diện"
                              {...props}
                            >
                              <IconUpload size={16} />
                            </ActionIcon>
                          )}
                        </FileButton>
                      )}
                      
                      {/* Nút chuyển đổi public/private cho admin */}
                      {isAdmin && (
                        <ActionIcon 
                          color={template.isPublic ? "yellow" : "gray"}
                          variant="subtle"
                          onClick={(e) => handleTogglePublic(template._id, !template.isPublic, e)}
                          title={template.isPublic ? "Đặt thành riêng tư" : "Đặt thành công khai"}
                        >
                          <IconWorld size={16} />
                        </ActionIcon>
                      )}
                    </Group>
                  }
                >
                  <div 
                    style={{ 
                      cursor: 'pointer', 
                      padding: '8px',
                      borderRadius: '4px',
                      backgroundColor: '#f9f9f9',
                      transition: 'background-color 0.2s',
                      display: 'flex',
                      gap: '12px'
                    }}
                    className="template-item"
                    onClick={() => handleApplyTemplate(template._id)}
                  >
                    {template.thumbnail ? (
                      <Image 
                        src={`${BASE_URL}public${template.thumbnail}`}
                        alt={template.name}
                        height={70} 
                        width={120} 
                        fit="cover"
                        radius="sm"
                        style={{ cursor: 'zoom-in' }}
                        onClick={(e) => openPreviewImage(`${BASE_URL}public${template.thumbnail}`, e)}
                      />
                    ) : (
                      <div 
                        style={{ 
                          height: 70, 
                          width: 120, 
                          backgroundColor: '#eee',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#aaa',
                          fontSize: '12px',
                          borderRadius: '4px'
                        }}
                      >
                        No thumbnail
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      <Group justify="space-between" wrap="nowrap">
                        <Text fw={500}>{template.name}</Text>
                        <Group gap={5}>
                          {/* Hiển thị badge số lượng biến trong template */}
                          {template.variables && template.variables.length > 0 && (
                            <Badge
                              color="green"
                              variant="light"
                            >
                              {template.variables.length} biến
                            </Badge>
                          )}
                          
                          {/* Hiển thị badge nếu là template public */}
                          {template.isPublic && (
                            <Tooltip
                              label={`Công khai bởi ${template.adminName || 'Admin'}`}
                              position="top"
                              withArrow
                            >
                              <Badge 
                                color="yellow" 
                                leftSection={<IconCrown size={12} />}
                                variant="light"
                              >
                                Public
                              </Badge>
                            </Tooltip>
                          )}
                          
                          {/* Hiển thị badge nếu người dùng là chủ sở hữu */}
                          {template.isOwner && (
                            <Badge 
                              color="blue" 
                              leftSection={<IconCheck size={12} />}
                              variant="light"
                            >
                              Của bạn
                            </Badge>
                          )}
                        </Group>
                      </Group>
                      <Text size="xs" c="dimmed">
                        Đã tạo: {formatDate(template.createdAt)}
                      </Text>
                      {template.isPublic && template.publicAt && (
                        <Text size="xs" c="dimmed">
                          Công khai: {formatDate(template.publicAt)}
                        </Text>
                      )}
                    </div>
                  </div>
                </List.Item>
              ))}
            </List>
          ) : (
            <Text c="dimmed" ta="center" py="md">
              Không có template nào. Hãy tạo và lưu template để hiển thị ở đây.
            </Text>
          )}
        </Stack>
      </Modal>

      <Modal
        opened={saveModalOpened}
        onClose={() => setSaveModalOpened(false)}
        title="Lưu mẫu"
        size="md"
      >
        <Stack>
          <TextInput
            label="Tên template"
            placeholder="Nhập tên template mới hoặc chọn template bên dưới để lưu đè..."
            value={selectedTemplate ? selectedTemplate.name : templateName}
            onChange={(e) => {
              if (selectedTemplate) {
                setSelectedTemplate({ ...selectedTemplate, name: e.target.value } as Template);
              } else {
                setTemplateName(e.target.value);
              }
            }}
            autoFocus
          />
          
          <Text size="sm" fw={500} mt="md">Templates của bạn:</Text>
          <Text size="xs" c="dimmed">Chọn một template nếu bạn muốn lưu đè lên template đó</Text>
          
          <div style={{ maxHeight: '250px', overflow: 'auto', padding: '8px', border: '1px solid #eee', borderRadius: '4px' }}>
            {templates.filter(template => template.isOwner).length > 0 ? (
              templates
                // Chỉ hiển thị templates của người dùng
                .filter(template => template.isOwner)
                .map(template => (
                  <div 
                    key={template._id}
                    style={{ 
                      padding: '8px', 
                      margin: '4px 0',
                      cursor: 'pointer',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                    className={`template-item ${selectedTemplateId === template._id ? 'selected' : ''}`}
                    onClick={() => handleSelectTemplate(template)}
                  >
                    {template.thumbnail ? (
                      <Image 
                        src={`${BASE_URL}public${template.thumbnail}`}
                        alt={template.name}
                        height={40} 
                        width={70} 
                        fit="cover"
                        radius="sm"
                      />
                    ) : (
                      <div 
                        style={{ 
                          height: 40, 
                          width: 70, 
                          backgroundColor: '#eee',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#aaa',
                          fontSize: '10px',
                          borderRadius: '4px'
                        }}
                      >
                        No thumbnail
                      </div>
                    )}
                    <div>
                      <Group>
                        <Text size="sm">{template.name}</Text>
                        {template.isPublic && (
                          <Badge 
                            color="yellow" 
                            size="xs"
                            variant="light"
                          >
                            Public
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed">
                        {formatDate(template.createdAt)}
                      </Text>
                    </div>
                  </div>
                ))
            ) : (
              <Text c="dimmed" ta="center" py="md">
                Không có template nào để lưu đè
              </Text>
            )}
          </div>
          
          <Group justify="space-between" align="center" mt="md">
            <Button 
              variant="subtle"
              color="red"
              onClick={() => {
                setSelectedTemplateId(null);
                setSelectedTemplate(null);
              }}
              disabled={!selectedTemplateId}
            >
              Bỏ chọn
            </Button>
            
            <Group>
              <Button variant="default" onClick={() => setSaveModalOpened(false)}>
                Hủy
              </Button>
              <Button 
                onClick={handleSaveTemplate}
                disabled={selectedTemplate ? !selectedTemplate.name.trim() : !templateName.trim()}
              >
                {selectedTemplateId ? 'Lưu đè' : 'Lưu mới'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* Modal xem ảnh */}
      <Modal
        opened={previewOpened}
        onClose={() => setPreviewOpened(false)}
        title="Hình ảnh template"
        size="lg"
        centered
      >
        {previewImage && (
          <Image
            src={previewImage}
            height="auto"
            width="100%"
            fit="contain"
            radius="md"
          />
        )}
      </Modal>
    </>
  );
} 